import type { OnDestroy } from '@angular/core';
import type { ThemeButton, ThemeSelectedButtons } from '@sites/site-theme/src/types/theme-button.type';

import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { EButtonStyleType } from '@common/enums';

import { BUTTON_STYLE_TYPE_TRANSLATIONS } from '@sites/site-theme/src/config/translation-keys.const';
import { defaultThemeButtonBase, defaultThemeButtonHover } from '@sites/site-theme/src/helpers/button-defaults';
import { THEME_BUTTON_STYLE_TYPES } from '@sites/site-theme/src/config/vocabularies.const';
import { ThemeSettings } from '@sites/site-theme/src/enums/theme-settings.enum';
import { ThemeStateService } from '@sites/site-theme/src/state';

import type { ThemeButtonEditorValue } from '@components/sp-custom-button-v2';

import { SpThemesButtonTabComponent } from './button-tab/button-tab.component';

type ButtonStyleType = EButtonStyleType.primary | EButtonStyleType.secondary;

@Component({
    selector: 'sp-theme-buttons-settings',
    templateUrl: './buttons-settings.component.html',
    styleUrls: ['./buttons-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpThemesButtonTabComponent, TranslateModule],
})
export class SpThemeButtonsSettingsComponent implements OnDestroy {
    private readonly themeState = inject(ThemeStateService);

    public readonly BUTTON_TYPES = THEME_BUTTON_STYLE_TYPES;
    public readonly BUTTON_STYLE_TYPE_TRANSLATIONS = BUTTON_STYLE_TYPE_TRANSLATIONS;

    public readonly themeColors = this.themeState.selectedColors;

    /** Which of the two tabs is open. */
    public readonly currentType = signal<ButtonStyleType>(EButtonStyleType.primary);

    private readonly themeButtons = computed<ThemeButton[]>(() => this.themeState.buttons()?.selected ?? []);

    /**
     * The button the open tab edits, found by its style type.
     *
     * By type rather than by the tab's index: the stored order is whatever the theme was saved
     * with, while the tabs are always `[primary, secondary]`, and `valueChangeHandler` has always
     * matched on the type. Indexing bound the wrong button whenever the two disagreed.
     */
    public readonly currentButton = computed(() => {
        const button = this.themeButtons().find((style) => style.styleType === this.currentType());

        if (!button) {
            return undefined;
        }

        // `base` and `hover` are both optional: a button that only ever had the deprecated
        // `styles`/`data` bags is preserved as found by `migrateWebsiteThemeButtonLegacy`, and the
        // editor below reads `base.borderWidth` while rendering, so passing `undefined` through
        // threw before the panel drew anything. Falling back to the shared defaults — the same ones
        // the AI tools use — shows such a button as editable instead of as a blank panel.
        const base = button.base ?? defaultThemeButtonBase();

        return { base, hover: button.hover ?? defaultThemeButtonHover(base) };
    });

    /** The buttons as they were when the panel opened, for cancel and for closing without saving. */
    private readonly initialButtons = signal<ThemeSelectedButtons | null>(null);
    private readonly isDirty = signal(false);
    private isSaved = false;

    constructor() {
        effect(() => {
            const buttons = this.themeState.buttons();

            if (buttons && !untracked(this.initialButtons)) {
                this.initialButtons.set(buttons);
            }
        });
    }

    public selectType(type: ButtonStyleType): void {
        this.currentType.set(type);
    }

    /**
     * The editor now speaks the stored shape, so nothing is converted here.
     *
     * v1 handed back bare numbers for the two lengths and this method re-attached `px` — a unit
     * decided by the panel rather than by the data. v2 edits `{ value, unit }` directly.
     */
    public valueChangeHandler({ base, hover }: ThemeButtonEditorValue): void {
        const type = this.currentType();

        this.isDirty.set(true);

        const selected = this.themeButtons().map((button) =>
            button.styleType === type ? { ...button, base, hover } : button
        );

        this.themeState.changeButtons({ selected, selectedIndex: null });
    }

    public onSave(): void {
        this.isSaved = true;
        this.themeState.unselectActiveSetting();
    }

    public onCancel(): void {
        this.restoreInitialButtons();
        this.themeState.unselectActiveSetting();
    }

    /**
     * The tab's "edit" link across to the font panel, which destroys THIS panel on the way.
     *
     * Keeping the edits is the point: switching panels is navigation, not a cancel, but
     * `ngOnDestroy` cannot tell the two apart and would restore the buttons the user just changed —
     * silently, since the drawer stays open on the font panel. Marking the edits kept here is the
     * same thing the Save button does, and it is why the link is handled by the parent at all.
     */
    public onEditFonts(): void {
        this.isSaved = true;
        this.themeState.setActiveSetting(ThemeSettings.fontsSettings);
    }

    public ngOnDestroy(): void {
        if (!this.isSaved) {
            this.restoreInitialButtons();
        }
    }

    /**
     * Puts the buttons back as they were, if the panel changed them.
     *
     * The dirty check used to be on `onCancel` only, so closing the drawer any other way dispatched
     * the initial buttons unconditionally — a write that changed nothing but still reached the
     * editor's autosave through `changes$`, marking a page dirty for a panel the user only looked at.
     */
    private restoreInitialButtons(): void {
        const initial = this.initialButtons();

        if (this.isDirty() && initial) {
            // Cleared so a cancel — which restores and THEN closes, reaching `ngOnDestroy` with
            // `isSaved` still false — writes the initial buttons once rather than twice.
            this.isDirty.set(false);
            this.themeState.changeButtons(initial);
        }
    }
}
